<script setup lang="ts">
import {computed} from 'vue';
import {
  BEAT_TYPES,
  COMMON_TIME_SIGNATURES,
  getTimeSignatureDisplayName,
} from '@/composables/musicXmlDom';
import Collapsible from '@/components/generic/Collapsible.vue';

// Taktart-Auswahl (Anzeige + Schnellwahl + eigene Taktart). Geteilt vom Taktart-Editor im
// Takt-Menü und vom Tab „Taktart" der Transpositions-Karte – reine Auswahl, kein Speichern.
const beats = defineModel<number>('beats', {required: true});
const beatType = defineModel<number>('beatType', {required: true});

const displayName = computed(() => getTimeSignatureDisplayName(beats.value, beatType.value));

function selectQuick(ts: { beats: number; beatType: number }) {
  beats.value = ts.beats;
  beatType.value = ts.beatType;
}

function isActive(ts: { beats: number; beatType: number }): boolean {
  return beats.value === ts.beats && beatType.value === ts.beatType;
}
</script>

<template>
  <div class="space-y-4">
    <div class="flex gap-2">
      <!-- Anzeige -->
      <div class="flex shrink-0 grow-1 flex-col items-center justify-center text-center p-2 bg-gradient-to-r from-sky-100 to-cyan-100 rounded-xl">
        <div class="flex grow text-2xl font-bold text-sky-800 font-mono leading-none">{{ displayName }}</div>
      </div>

      <!-- Schnellauswahl -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Schnellauswahl</label>
        <div class="flex flex-wrap gap-2">
          <button
              v-for="ts in COMMON_TIME_SIGNATURES"
              :key="`${ts.beats}-${ts.beatType}`"
              type="button"
              @click="selectQuick(ts)"
              class="px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors font-mono"
              :class="isActive(ts)
                ? 'bg-sky-100 border-sky-300 text-sky-700'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
          >
            {{ ts.beats }}/{{ ts.beatType }}
          </button>
        </div>
      </div>
    </div>

    <!-- Detail-Editor (eingeklappt; Anzeige + Schnellauswahl decken den Normalfall ab) -->
    <Collapsible variant="default" class="text-sm text-blue-500" :is-open="false">
      <template #header>
        <h5 class="text-lg">Eigene Taktart</h5>
      </template>
      <template #default>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Zähler
            <span class="text-gray-400 font-normal ml-2">({{ beats }})</span>
          </label>
          <input
              type="range"
              min="1"
              max="16"
              step="1"
              v-model.number="beats"
              class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-sky-500"
          />
        </div>

        <div class="mt-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Nenner (Notenwert)</label>
          <div class="grid grid-cols-5 gap-1">
            <button
                v-for="bt in BEAT_TYPES"
                :key="bt"
                type="button"
                @click="beatType = bt"
                class="py-2 text-sm font-medium rounded border transition-colors text-center font-mono"
                :class="beatType === bt
                  ? 'bg-sky-500 border-sky-500 text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'"
            >
              {{ bt }}
            </button>
          </div>
        </div>
      </template>
    </Collapsible>
  </div>
</template>
